import React, { useState, useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const firebaseConfig = {
  apiKey: "AIzaSyBoA7sBhG7h-e7JSPq88K0abYFQRQ_nloU",
  authDomain: "trading-sim-5b456.firebaseapp.com",
  databaseURL: "https://trading-sim-5b456-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "trading-sim-5b456",
  storageBucket: "trading-sim-5b456.firebasestorage.app",
  messagingSenderId: "102699961766",
  appId: "1:102699961766:web:5de64337c4bee854db1367",
  measurementId: "G-ZCXVS2HZDK"
};

const DEFAULT_SETTINGS = {
    strategy: "0DTE Credit Spreads",
    tradeWindow: "10:00 - 14:00 ET",
    rsiPutThresh: 40,
    rsiCallThresh: 60,
    riskPerTradePct: 2.0,
    feesPerContract: 1.30,
    slippagePerContract: 2.00
};

function App() {
  const [balance, setBalance] = useState(10000.00);
  const [activeTrade, setActiveTrade] = useState(null);
  const [logs, setLogs] = useState([]);
  const [latestRSI, setLatestRSI] = useState(50);
  const [equityCurve, setEquityCurve] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  
  useEffect(() => {
    const app = initializeApp(firebaseConfig);
    const db = getFirestore(app);
    
    const unsub = onSnapshot(doc(db, "system", "state"), (doc) => { 
       if(doc.exists()) {
           const data = doc.data();
           setBalance(data.paper_balance || 10000);
           setActiveTrade(data.active_trade);
           setLogs(data.logs || []);
           if (data.logs && data.logs.length > 0) setLatestRSI(data.logs[0].rsi);
           if (data.equity_curve) {
               const formattedData = data.equity_curve.map((point) => ({
                   name: new Date(point.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}),
                   balance: point.balance
               }));
               setEquityCurve(formattedData);
           }
           setSettings(data.settings || DEFAULT_SETTINGS);
       }
    });
    
    return () => unsub();
  }, []);

  const handleKillSwitch = async () => {
    try {
      const functions = getFunctions();
      const killSwitch = httpsCallable(functions, 'killSwitch');
      await killSwitch();
      
      setLogs(prev => [{
        time: new Date().toISOString(),
        message: "🚨 KILL SWITCH ACTIVATED. ALL TRADES CLOSED."
      }, ...prev]);
      setActiveTrade(null);
    } catch (error) {
      console.error("Kill switch failed:", error);
      alert("Failed to activate Kill Switch. Check console.");
    }
  };

  const getIdleStatus = () => {
    const now = new Date();
    const etTime = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const parts = Object.fromEntries(etTime.map(p => [p.type, p.value]));
    const day = parts.weekday;
    const hour = parseInt(parts.hour, 10);
    const minute = parseInt(parts.minute, 10);
    const totalMinutes = hour * 60 + minute;

    if (day === 'Sat' || day === 'Sun') {
      return { icon: '📅', text: 'Weekend — market is closed', color: 'var(--text-muted)' };
    }
    if (totalMinutes < 10 * 60) {
      return { icon: '🌅', text: `Pre-market — bot activates at 10:00 AM ET (currently ${parts.hour}:${parts.minute} ET)`, color: 'var(--accent-blue)' };
    }
    if (totalMinutes >= 14 * 60) {
      return { icon: '🌙', text: `After-hours — trading window closed at 2:00 PM ET (currently ${parts.hour}:${parts.minute} ET)`, color: 'var(--text-muted)' };
    }
    return { icon: '⏳', text: `Bot is running — waiting for a 5-minute candle close & RSI signal...`, color: 'var(--accent-green)' };
  };

  const getRSIColor = (rsi) => {
    if (rsi > 60) return 'var(--accent-red)';
    if (rsi < 40) return 'var(--accent-green)';
    return 'var(--accent-blue)';
  };

  const renderTooltip = ({ active, payload }) => {
      if (active && payload && payload.length) {
          return (
              <div style={{ background: 'var(--panel-bg)', padding: '10px', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                  <p style={{ margin: 0, color: 'var(--text-main)' }}>{payload[0].payload.name}</p>
                  <p style={{ margin: 0, color: 'var(--accent-green)', fontWeight: 'bold' }}>
                      ${payload[0].value.toFixed(2)}
                  </p>
              </div>
          );
      }
      return null;
  };

  return (
    <BrowserRouter basename="/trading-sim">
      <Routes>
        <Route path="/" element={
          <div className="dashboard-container">
            <header className="header">
              <h1>NVDA Alpha Engine</h1>
              <button className="kill-switch" onClick={handleKillSwitch}>
                ⏻ KILL SWITCH
              </button>
            </header>

            <div className="main-grid">
              <div className="left-col">
                <div className="glass-panel metric-box">
                  <div className="metric-title">Paper Balance</div>
                  <div className="metric-value">${balance.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
                  
                  <div style={{ marginTop: '2.5rem' }}>
                    <div className="metric-title">Active Trade Status</div>
                    <div style={{ fontSize: '1.2rem', fontWeight: 600, color: activeTrade ? 'var(--accent-blue)' : 'var(--text-muted)' }}>
                      {activeTrade ? (
                        <div>
                          <div>{activeTrade.type} @ ${activeTrade.entryPrice.toFixed(2)}</div>
                          {activeTrade.short_strike && (
                            <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '0.4rem', fontWeight: 400 }}>
                              Strikes: {activeTrade.short_strike.toFixed(2)}/{activeTrade.long_strike.toFixed(2)} | {activeTrade.contracts} Contracts @ ${activeTrade.entry_credit.toFixed(2)} Credit
                            </div>
                          )}
                        </div>
                      ) : 'Waiting for Setup...'}
                    </div>
                  </div>
                </div>

                <div className="glass-panel rsi-gauge-container">
                  <div className="metric-title" style={{ marginBottom: '1rem' }}>Live 5m RSI (14)</div>
                  <div 
                    className="rsi-circle" 
                    style={{ 
                      borderColor: getRSIColor(latestRSI),
                      boxShadow: `0 0 40px ${getRSIColor(latestRSI)}40`,
                      color: getRSIColor(latestRSI)
                    }}
                  >
                    {latestRSI ? latestRSI.toFixed(0) : '50'}
                  </div>
                </div>

                <div className="glass-panel settings-panel">
                  <div className="metric-title" style={{ marginBottom: '1rem', textAlign: 'center' }}>Active Settings</div>
                  {settings ? (
                      <div className="settings-grid">
                          <div className="setting-item"><span>Strategy:</span> <strong>{settings.strategy}</strong></div>
                          <div className="setting-item"><span>Time:</span> <strong>{settings.tradeWindow}</strong></div>
                          <div className="setting-item"><span>Bull Put RSI:</span> <strong style={{color:'var(--accent-green)'}}>&lt; {settings.rsiPutThresh}</strong></div>
                          <div className="setting-item"><span>Bear Call RSI:</span> <strong style={{color:'var(--accent-red)'}}>&gt; {settings.rsiCallThresh}</strong></div>
                          <div className="setting-item"><span>Risk/Trade:</span> <strong>{settings.riskPerTradePct}%</strong></div>
                          <div className="setting-item"><span>Round-trip Fee:</span> <strong>${settings.feesPerContract}</strong></div>
                          <div className="setting-item"><span>Slippage Pen.:</span> <strong>${settings.slippagePerContract}</strong></div>
                      </div>
                  ) : (
                      <div style={{color:'var(--text-muted)', textAlign:'center'}}>Loading settings...</div>
                  )}
                </div>
              </div>

              <div className="right-col">
                <div className="glass-panel" style={{ height: '350px', display: 'flex', flexDirection: 'column' }}>
                  <div className="metric-title" style={{ marginBottom: '1rem' }}>Equity Curve (Realized)</div>
                  <div style={{ flex: 1, width: '100%' }}>
                      <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={equityCurve} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                              <XAxis dataKey="name" stroke="var(--text-muted)" tick={{fontSize: 12}} />
                              <YAxis domain={['auto', 'auto']} stroke="var(--text-muted)" tick={{fontSize: 12}} tickFormatter={(val) => `$${val}`} />
                              <Tooltip content={renderTooltip} />
                              <Line type="monotone" dataKey="balance" stroke="var(--accent-green)" strokeWidth={3} dot={false} activeDot={{ r: 6 }} />
                          </LineChart>
                      </ResponsiveContainer>
                  </div>
                </div>

                <div className="glass-panel" style={{ flex: 1, minHeight: '350px' }}>
                  <div className="metric-title" style={{ marginBottom: '1.5rem' }}>Engine Logs & Activity</div>
                  <div className="logs-container" style={{ height: '300px' }}>
                    {logs.length === 0 ? (() => { const s = getIdleStatus(); return <p style={{ color: s.color, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><span style={{ fontSize: '1.2rem' }}>{s.icon}</span>{s.text}</p>; })() : null}
                    {logs.map((log, i) => (
                      <div className="log-item" key={i}>
                        <div className="log-time">{new Date(log.time).toLocaleTimeString()}</div>
                        <div className="log-msg" style={{ 
                          color: log.message.includes('KILL SWITCH') || log.message.includes('Stop Loss') ? 'var(--accent-red)' : 
                                 log.message.includes('Signal') || log.message.includes('Take Profit') ? 'var(--accent-green)' : 'var(--text-main)',
                          fontWeight: log.message.includes('Signal') || log.message.includes('KILL') || log.message.includes('CLOSED') ? 600 : 400
                        }}>
                          {log.message}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
